import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";
import { api } from "../api";

import { Card, Btn, Notice } from "../components/ui";

const cell = { padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" };

export default function TenderEvaluationsPage() {
  const [pending, setPending] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  // Owned here so the submit confirmation survives navigating back to the list.
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => { setLoading(true); api.getPendingEvaluations().then(setPending).catch(e => setNotice({ kind: "error", text: e.message })).finally(() => setLoading(false)); };
  useEffect(load, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;
  if (sel) { const t = pending.find(x => x.id === sel); if (t) return <ScoreGrid tender={t} onBack={() => setSel(null)} done={() => { setSel(null); load(); }} notice={notice} notify={setNotice} />; }

  return (
    <div>
      <h1 style={{ fontSize: text.h1, fontWeight: 700, margin: "0 0 4px" }}>Pending Evaluations</h1>
      <p style={{ fontSize: text.md, color: C.gray500, margin: "0 0 16px" }}>{pending.length} evaluation(s) awaiting your scoring</p>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Evaluation No.", "Title", "Bids", "Closing", ""].map((h, i) => <th scope="col" key={i} style={cell}>{h || <span className="sr-only">Actions</span>}</th>)}</tr></thead>
          <tbody>
            {pending.map(t => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{t.evaluationNumber}</td>
                <td style={{ padding: "10px 12px" }}>{t.title}</td>
                <td style={{ padding: "10px 12px" }}>{t.bids?.length}</td>
                <td style={{ padding: "10px 12px" }}>{t.closingDate ? new Date(t.closingDate).toLocaleDateString() : "—"}</td>
                <td style={{ padding: "10px 12px" }}><Btn onClick={() => { setNotice(null); setSel(t.id); }}>Evaluate</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ScoreGrid({ tender, onBack, done, notice, notify }) {
  const qual = (tender.criteria || []).filter(c => !c.isPrice);
  const [grid, setGrid] = useState({}); // key bidId|criterionId -> score
  const [coi, setCoi] = useState({});   // key bidId -> bool
  const set = (b, c, v) => setGrid({ ...grid, [`${b}|${c}`]: v });

  const submit = async () => {
    const scores = [];
    for (const b of tender.bids) for (const c of qual) {
      scores.push({ bidId: b.id, criterionId: c.id, score: Number(grid[`${b.id}|${c.id}`]), coiDeclared: !!coi[b.id] });
    }
    // The parent owns the notice, so the confirmation survives done() → list;
    // on failure the entered scores stay in the grid.
    try {
      const r = await api.submitEvaluation(tender.id, { scores });
      notify({ kind: "success", text: r.message });
      // Tell the shell the workqueue changed so the sidebar badge refreshes.
      window.dispatchEvent(new Event("workqueue-changed"));
      done();
    }
    catch (e) { notify({ kind: "error", text: e.message }); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Btn variant="outline" onClick={onBack}>← Back</Btn>
        <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{tender.evaluationNumber} — {tender.title}</h1>
      </div>
      {/* What is being evaluated (UX-53): the grid alone is a context vacuum —
          carry the requirement, source documents and money alongside it. */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>What you are evaluating</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, fontSize: text.md }}>
          <div style={{ gridColumn: "1 / -1" }}><span style={{ color: C.gray500 }}>Requirement:</span> <strong>{tender.title}</strong>{tender.aor?.description && tender.aor.description !== tender.title ? <span style={{ color: C.gray600 }}> — {tender.aor.description}</span> : null}</div>
          {tender.tenderRequest && <div><span style={{ color: C.gray500 }}>Tender:</span> <strong>{tender.tenderRequest.itqNumber || tender.tenderRequest.requestNumber} ({tender.tenderRequest.type})</strong></div>}
          <div><span style={{ color: C.gray500 }}>Source AOR:</span> <strong>{tender.aor?.aorNumber || "—"}</strong></div>
          <div><span style={{ color: C.gray500 }}>Budget:</span> <strong>{tender.budgetRef} · {fmt(tender.total)}</strong></div>
          <div><span style={{ color: C.gray500 }}>Closing:</span> <strong>{tender.closingDate ? new Date(tender.closingDate).toLocaleDateString() : "—"}</strong></div>
          <div><span style={{ color: C.gray500 }}>Requested by:</span> <strong>{tender.requestor?.displayName || "—"}</strong></div>
        </div>
        <div style={{ fontSize: text.sm, color: C.gray500, marginTop: 8 }}>
          Bid documents are not held in this system — request them from the contact on the tender if you need more than vendor and price.
        </div>
      </Card>
      <Card>
        <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 10 }}>
          Score each bid 0–100 on every qualitative criterion. Price is auto-scored and shown for reference.
          <br />
          <strong>COI</strong> = conflict of interest: tick it if you have a personal or financial connection to that vendor.
          It is recorded with your scores for the audit trail — it does not exclude your scores automatically.
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Vendor", "Price (ref)", ...qual.map(c => `${c.name} (${c.weight}%)`), "COI"].map((h, i) => <th scope="col" key={i} style={cell}>{h}</th>)}</tr></thead>
          <tbody>
            {tender.bids.map(b => (
              <tr key={b.id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "8px 12px", fontWeight: 600 }}>{b.vendorName}</td>
                <td style={{ padding: "8px 12px", color: C.gray500 }}>{fmt(b.price)}</td>
                {qual.map(c => (
                  <td key={c.id} style={{ padding: "8px 12px", width: 110 }}>
                    <input type="number" min="0" max="100" aria-label={`${b.vendorName} — ${c.name} score`} value={grid[`${b.id}|${c.id}`] ?? ""} onChange={e => set(b.id, c.id, e.target.value)} style={{ width: "100%", padding: 6, border: `1px solid ${C.gray300}`, borderRadius: 4 }} />
                  </td>
                ))}
                <td style={{ padding: "8px 12px" }}><input type="checkbox" aria-label={`Conflict of interest with ${b.vendorName}`} checked={!!coi[b.id]} onChange={e => setCoi({ ...coi, [b.id]: e.target.checked })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 16 }}>
          {/* Anchored to the action row so errors appear where the user acted. */}
          <Notice notice={notice} onDismiss={() => notify(null)} />
          <Btn onClick={submit}>Submit evaluation</Btn>
        </div>
      </Card>
    </div>
  );
}
